import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Room, Player } from "@/hooks/useRoom";
import { supabase } from "@/integrations/supabase/client";
import { LogOut, DoorOpen } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface Props {
  room: Room;
  me: Player;
  amHost: boolean;
  players: Player[];
}

export default function RoomHeader({ room, me, amHost, players }: Props) {
  const navigate = useNavigate();
  const [transferOpen, setTransferOpen] = useState(false);
  const [selectedHeir, setSelectedHeir] = useState<string>("");
  const [busy, setBusy] = useState(false);

  const others = players.filter((p) => p.id !== me.id);

  const closeRoomForEveryone = async () => {
    // Deleting the room cascades the UI: all clients see room=null and get redirected.
    await supabase.from("rooms").delete().eq("id", room.id);
  };

  const onLeaveClick = (e: React.MouseEvent) => {
    if (amHost) {
      e.preventDefault();
      setSelectedHeir(others[0]?.id ?? "");
      setTransferOpen(true);
    }
  };

  const leaveAsNonHost = async () => {
    await supabase.from("players").delete().eq("id", me.id);
    navigate("/");
  };

  const transferAndLeave = async () => {
    if (!selectedHeir) return;
    setBusy(true);
    // Promote new host first, then demote + remove self.
    const { error: promoteErr } = await supabase
      .from("players")
      .update({ is_host: true })
      .eq("id", selectedHeir);
    if (promoteErr) {
      setBusy(false);
      toast.error("Couldn't transfer host");
      return;
    }
    const newHostPlayer = players.find((p) => p.id === selectedHeir);
    if (newHostPlayer) {
      await supabase.from("rooms").update({ host_id: newHostPlayer.client_id }).eq("id", room.id);
    }
    await supabase.from("players").update({ is_host: false }).eq("id", me.id);
    await supabase.from("players").delete().eq("id", me.id);
    setBusy(false);
    setTransferOpen(false);
    navigate("/");
  };

  const closeAndLeave = async () => {
    setBusy(true);
    await closeRoomForEveryone();
    setBusy(false);
    setTransferOpen(false);
    navigate("/");
  };

  const endGame = async () => {
    await supabase.from("rooms").update({ status: "finished" }).eq("id", room.id);
  };

  return (
    <div className="sticky top-0 z-30 -mx-4 mb-2 px-4 py-2.5 backdrop-blur bg-background/80 border-b">
      <div className="max-w-2xl mx-auto flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-xs uppercase tracking-wider text-muted-foreground truncate">
            {room.mode === "game" ? "SameBrain" : "Scrum Poker"}
          </div>
          <div className="text-sm font-extrabold tracking-[0.2em]">{room.code}</div>
        </div>
        <div className="flex items-center gap-1">
          <ThemeToggle className="h-8 w-8" />
          {amHost && room.status !== "lobby" && room.status !== "finished" && (
            <AlertDialog>
              <AlertDialogTrigger className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                <DoorOpen className="h-3.5 w-3.5" /> End game
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>End the game for everyone?</AlertDialogTitle>
                  <AlertDialogDescription>
                    All players will be sent to the final scoreboard.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={endGame}>End game</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {amHost ? (
            <button
              onClick={onLeaveClick}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Leave
            </button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                <LogOut className="h-3.5 w-3.5" /> Leave
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Leave this room?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You can rejoin anytime with the room code.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Stay</AlertDialogCancel>
                  <AlertDialogAction onClick={leaveAsNonHost}>Leave room</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Host-leave dialog */}
      <Dialog open={transferOpen} onOpenChange={(o) => !busy && setTransferOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>You're the host — pass the torch?</DialogTitle>
            <DialogDescription>
              {others.length > 0
                ? "Pick someone to take over as host. If you'd rather end the room for everyone, you can close it instead."
                : "You're the only one here. Leaving will close the room for good."}
            </DialogDescription>
          </DialogHeader>

          {others.length > 0 && (
            <RadioGroup
              value={selectedHeir}
              onValueChange={setSelectedHeir}
              className="max-h-64 overflow-y-auto space-y-1 pr-1"
            >
              {others.map((p) => (
                <Label
                  key={p.id}
                  htmlFor={`heir-${p.id}`}
                  className="flex items-center gap-2 rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-muted/50"
                >
                  <RadioGroupItem id={`heir-${p.id}`} value={p.id} />
                  <span className="text-sm font-medium truncate">{p.name}</span>
                </Label>
              ))}
            </RadioGroup>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setTransferOpen(false)}
              disabled={busy}
            >
              Stay
            </Button>
            <Button
              variant="destructive"
              onClick={closeAndLeave}
              disabled={busy}
            >
              Close room for everyone
            </Button>
            {others.length > 0 && (
              <Button
                onClick={transferAndLeave}
                disabled={busy || !selectedHeir}
              >
                Transfer host & leave
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
